import { useTranslation } from 'react-i18next';
import { fmtNumber } from '../format';

const fmtDay = (day) => new Date(`${day}T00:00:00`).toLocaleDateString();

/**
 * GoalRush kicks per day as a card beside "AR filters selected" on the all-modules view, in the
 * same visual language (EffectUsageSummary). The list scrolls inside the card so the row it
 * shares with the AR card is sized by that card; "View more" opens the full analytics drawer.
 */
const GoalRushDailyCard = ({ daily, loading, onViewMore }) => {
  const { t } = useTranslation();
  const k = (key, opts) => t(`admin.insights.goalrushStats.${key}`, opts);

  // Newest day first, so the latest activity is at the top.
  const rows = [...(daily || [])].reverse();
  const maxKicks = Math.max(1, ...rows.map((d) => d.kicks || 0));

  return (
    <div className="h-full min-h-[280px] bg-white rounded-xl border border-[#E2E8F0] overflow-hidden flex flex-col">
      <div className="px-5 py-3.5 border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <p className="text-[13px] font-semibold text-[#0F172A]">{k('dailyCardTitle')}</p>
        {/* Subtitle hidden. Uncomment to switch back.
        <p className="text-[11px] text-[#94A3B8]">{k('dailyHint')}</p>
        */}
      </div>

      {/* Absolutely positioned so the list never adds height of its own: the card takes the
          row's height (set by the AR card beside it, or its min-height) and the list scrolls. */}
      <div className="relative flex-1">
        <div className="absolute inset-0 overflow-y-auto py-2">
          {loading ? (
            <div className="px-5 py-1 space-y-3 animate-pulse">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-6 w-full bg-[#F1F5F9] rounded" />
              ))}
            </div>
          ) : !rows.length ? (
            <p className="px-5 py-2.5 text-[13px] text-[#94A3B8]">{k('empty')}</p>
          ) : (
            rows.map((d) => (
              <div key={d.day} className="flex items-center gap-3 px-5 py-2.5">
                <span
                  className="shrink-0 rounded-full bg-[#10B981]"
                  style={{ width: 6, height: 6 }}
                />
                <span className="shrink-0 text-[13px] font-medium text-[#334155] w-[90px] truncate">
                  {fmtDay(d.day)}
                </span>
                <span className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                  <span
                    className="block h-full rounded-full bg-[#10B981]"
                    style={{ width: `${Math.max(4, ((d.kicks || 0) / maxKicks) * 100)}%` }}
                  />
                </span>
                <span className="shrink-0 text-[13px] font-semibold text-[#0F172A] tabular-nums w-20 text-right">
                  {k('kicksCount', { value: fmtNumber(d.kicks) })}
                </span>
                <span className="shrink-0 text-[12px] text-[#64748B] tabular-nums w-16 text-right">
                  {k('goalsCount', { value: fmtNumber(d.goals) })}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="px-5 py-2.5 border-t border-[#E2E8F0]">
        <button
          type="button"
          onClick={onViewMore}
          className="text-[12px] font-semibold text-[#3B5BDB] hover:text-[#2F4AC0] transition-colors"
        >
          {k('viewMore')}
        </button>
      </div>
    </div>
  );
};

export default GoalRushDailyCard;
